import React from 'react';
import './SidePanel.css';

const SCREEN_CONTENT = {
  properties: ({ currentProperty }) => ({
    uxObjective: 'Enable quick discovery of Nemacolin\'s distinct lodging options.',
    userActions: 'Swipe through the property carousel and tap a card to dive into room availability.',
    designHighlights: 'Edge-to-edge imagery, carousel snap points, and subtle card elevation reinforce a premium feel.'
  }),
  rooms: ({ currentProperty }) => ({
    uxObjective: `Help guests compare room types within ${currentProperty.name} at-a-glance.`,
    userActions: 'Filter, sort, and tap a room card for more details or pricing breakdown.',
    designHighlights: 'Sticky date / guest bar, real-time pricing chips, and "peek" animation on scroll.'
  }),
  roomDetails: () => ({
    uxObjective: 'Surface detailed imagery, amenities, and rate options without overwhelming the user.',
    userActions: 'Swipe photo gallery, expand description, pick a rate, then tap Confirm.',
    designHighlights: 'Full-screen sheet, parallax hero, and inline Apple Pay price cards.'
  }),
  priceBreakdown: () => ({
    uxObjective: 'Build cost transparency and trust by itemising nightly rates, taxes, and fees.',
    userActions: 'Review breakdown, scroll for cancellation policy, close to return.',
    designHighlights: 'Accordion sections, muted tax rows, and pinned "Total" footer.'
  }),
  calendar: () => ({
    uxObjective: 'Let guests visualise price fluctuations across dates.',
    userActions: 'Pan calendar, tap two dates to select range.',
    designHighlights: 'Heat-map colour coding, inline month switcher, and haptic-like hover states.'
  }),
  confirmation: () => ({
    uxObjective: 'Provide a final review step with seamless payment.',
    userActions: 'Double-check summary, hit Apple Pay, change payment if needed.',
    designHighlights: 'Native Apple Pay button, minimal form fields, and reassurance-focused copy.'
  }),
  final: () => ({
    uxObjective: 'Celebrate the booking success and set expectations for next steps.',
    userActions: 'Read confirmation, tap Done to return home.',
    designHighlights: 'Confetti-like success icon, concise summary card, and follow-up callout.'
  }),
  default: () => ({
    uxObjective: 'Explore the prototype flow.',
    userActions: 'Interact with UI elements to proceed.',
    designHighlights: 'Consistent iOS styling and motion.'
  })
};

const SidePanel = ({ currentProperty, currentScreen, checkinDate, checkoutDate, isVisible, onClose }) => {
  const getScreenTitle = () => {
    switch (currentScreen) {
      case 'properties':
        return 'Property Browser';
      case 'rooms':
        return 'Room Grid';
      case 'roomDetails':
        return 'Room Details Sheet';
      case 'priceBreakdown':
        return 'Price Breakdown';
      case 'calendar':
        return 'Rate Calendar';
      case 'confirmation':
        return 'Payment & Review';
      case 'final':
        return 'Success Screen';
      default:
        return 'Nemacolin Prototype';
    }
  };

  const { uxObjective } = (SCREEN_CONTENT[currentScreen] || SCREEN_CONTENT.default)({ currentProperty });

  const computeActions = () => {
    switch (currentScreen) {
      case 'properties':
        return [
          'Swipe left/right to browse properties',
          'Tap Book Now to view rooms for the highlighted property',
          'Tap the date banner to open calendar'
        ];
      case 'rooms':
        return [
          'Scroll to explore room cards',
          'Tap Filters to refine results',
          'Tap a room to see details'
        ];
      case 'roomDetails':
        return [
          'Swipe gallery images',
          'Toggle Read More/Read Less',
          'Tap Rate details & policies',
          'Tap Price breakdown for detailed pricing',
          'Select a rate and Confirm'
        ];
      case 'priceBreakdown':
        return [
          'Scroll to review charges',
          'Tap Close to return'
        ];
      case 'calendar':
        return [
          'Tap dates to set check-in/out',
          'Navigate months with arrows',
          'Adjust guests',
          'Continue or Back'
        ];
      case 'confirmation':
        return [
          'Review booking summary',
          'Tap Price breakdown for cost details',
          'Change Payment method',
          'Tap Book Now to finalize the reservation'
        ];
      case 'final':
        return [
          'Review confirmation',
          'Tap Done to finish'
        ];
      default:
        return [];
    }
  };

  const sections = [
    { title: 'UX Objective', body: uxObjective },
    { title: 'Actions Available', items: computeActions() }
  ];

  const renderSectionContent = (sec) => {
    if (sec.items && Array.isArray(sec.items)) {
      return (
        <ul className="side-panel-list">
          {sec.items.map((item, idx) => <li key={idx}>{item}</li>)}
        </ul>
      );
    }
    return <p>{sec.body}</p>;
  };

  return (
    <>
      <div 
        className={`side-panel-overlay ${isVisible ? 'visible' : ''}`} 
        onClick={onClose}
      ></div>
      <div className={`side-panel ${isVisible ? 'visible' : ''}`}>
        <button onClick={onClose} className="side-panel-close-button">&times;</button>
        <div className="side-panel-header">
          <img 
            src="https://ambitioninnovationcreativity.com/static/media/AIC%20LOGO%20NAVBAR.68327634.svg" 
            alt="Ambition Innovation Creativity Logo" 
            className="side-panel-logo"
          />
          <h4 className="side-panel-company-name">Ambition Innovation Creativity</h4>
          <p className="side-panel-subtitle">Nemacolin - Guest Booking Flow Proposal</p>
          <p className="side-panel-purpose">
            <strong>Disclaimer:</strong> This is a high-fidelity prototype demonstrating a proposed user experience. It utilizes Nemacolin's live calendar and availability APIs for realism, but it is <strong>not a real app and will not process bookings.</strong>
            <br/><br/>
            If approved, the final UI/UX will be adapted to native iOS and Android design standards. This simulation was built with AIC's rapid web prototyping tools for demonstration purposes only.
          </p>
        </div>
        <hr className="side-panel-divider" />
        <div className="side-panel-dynamic-content">
          <h3>{getScreenTitle()}</h3>
          {sections.map((sec, idx) => (
            <div className="info-section" key={idx}>
              <h4>{sec.title}</h4>
              {renderSectionContent(sec)}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default SidePanel;